import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import { ArrowLeft, Image as ImageIcon, AlertCircle } from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";

import {
  PageHeader,
  Card,
  Input,
  Textarea,
  NumberInput,
  FormField,
  TagInput,
  Button,
  Spinner,
} from "../../design-system";

class StageForm extends Component {
  constructor(props) {
    super(props);
    const { id } = this.props.match.params;
    this.state = {
      id: id || null,
      title: "",
      slug: "",
      company: "",
      companyAddress: "",
      companyPhone: "",
      companyEmail: "",
      companyWebsite: "",
      period: "",
      description: "",
      technologies: [],
      frameworks: [],
      databases: [],
      projects: [],
      imageUrl: "",
      order: 0,
      loading: false,
      saving: false,
      imagePreview: null,
      imageError: false,
    };
  }

  handleImageUrlChange = (e) => {
    const imageUrl = e.target.value;
    this.setState({ imageUrl, imageError: false, imagePreview: imageUrl || null });
    if (imageUrl) {
      const img = document.createElement("img");
      img.onload = () => this.setState({ imagePreview: imageUrl, imageError: false });
      img.onerror = () => this.setState({ imagePreview: null, imageError: true });
      img.src = imageUrl;
    }
  };

  componentDidMount() {
    const token = localStorage.getItem("jwtToken");
    if (token) setAuthToken(token);
    if (this.state.id) this.loadStage();
  }

  loadStage = async () => {
    try {
      this.setState({ loading: true });
      const response = await axios.get(`/api/stages/id/${this.state.id}`);
      const stage = response.data.stage;
      this.setState({
        title: stage.title || "",
        slug: stage.slug || "",
        company: stage.company || "",
        companyAddress: stage.companyAddress || "",
        companyPhone: stage.companyPhone || "",
        companyEmail: stage.companyEmail || "",
        companyWebsite: stage.companyWebsite || "",
        period: stage.period || "",
        description: stage.description || "",
        technologies: Array.isArray(stage.technologies) ? stage.technologies : [],
        frameworks: Array.isArray(stage.frameworks) ? stage.frameworks : [],
        databases: Array.isArray(stage.databases) ? stage.databases : [],
        projects: Array.isArray(stage.projects) ? stage.projects : [],
        imageUrl: stage.imageUrl || "",
        order: stage.order || 0,
        loading: false,
        imagePreview: stage.imageUrl || null,
        imageError: false,
      });
    } catch (error) {
      console.error("Error loading stage:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur", message: "Impossible de charger le stage",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  generateSlug = (title) =>
    title.toLowerCase()
      .normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  handleTitleChange = (e) => {
    const title = e.target.value;
    this.setState({ title });
    if (!this.state.slug || this.state.slug === this.generateSlug(this.state.title)) {
      this.setState({ slug: this.generateSlug(title) });
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ saving: true });
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);

      const stageData = {
        title: this.state.title,
        slug: this.state.slug,
        company: this.state.company,
        companyAddress: this.state.companyAddress,
        companyPhone: this.state.companyPhone,
        companyEmail: this.state.companyEmail,
        companyWebsite: this.state.companyWebsite,
        period: this.state.period,
        description: this.state.description,
        technologies: this.state.technologies,
        frameworks: this.state.frameworks,
        databases: this.state.databases,
        projects: this.state.projects,
        imageUrl: this.state.imageUrl,
        order: parseInt(this.state.order) || 0,
      };

      if (this.state.id) await axios.put(`/api/stages/${this.state.id}`, stageData);
      else                await axios.post("/api/stages", stageData);

      Store.addNotification({
        title: `Stage ${this.state.id ? "modifié" : "créé"}`,
        message: `« ${this.state.title} » a été enregistré.`,
        type: "success",
        insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      this.props.history.push("/dashboard/stages");
    } catch (error) {
      console.error("Error saving stage:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Erreur lors de la sauvegarde",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
      this.setState({ saving: false });
    }
  };

  render() {
    const { loading, saving } = this.state;
    const isEdit = !!this.state.id;
    const pageTitle = isEdit ? "Modifier le stage" : "Nouveau stage";

    if (loading) {
      return (
        <AdminPageShell pageTitle={pageTitle}>
          <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
        </AdminPageShell>
      );
    }

    return (
      <AdminPageShell pageTitle={pageTitle} breadcrumbLast={isEdit ? (this.state.title || "Modifier") : undefined}>
        <PageHeader
          eyebrow="— Stages"
          title={isEdit ? "Modifier le stage" : "Nouveau stage"}
          description={isEdit ? "Mets à jour les informations de ce stage." : "Ajoute une nouvelle expérience à ton parcours."}
          actions={
            <Button
              variant="secondary"
              leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={1.8} />}
              onClick={() => this.props.history.push("/dashboard/stages")}
            >
              Retour
            </Button>
          }
        />

        <form onSubmit={this.handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-5">
              <Card>
                <Card.Header>
                  <Card.Title>Informations principales</Card.Title>
                </Card.Header>
                <div className="space-y-4">
                  <FormField label="Titre" required>
                    <Input value={this.state.title} onChange={this.handleTitleChange} required autoFocus />
                  </FormField>
                  <FormField label="Slug" required helper="Identifiant URL, en kebab-case (a-z, 0-9, -).">
                    <Input
                      value={this.state.slug}
                      onChange={(e) => this.setState({ slug: e.target.value })}
                      pattern="[a-z0-9-]+"
                      required
                    />
                  </FormField>
                  <FormField label="Période" required helper="Ex. 2018-2019.">
                    <Input
                      value={this.state.period}
                      onChange={(e) => this.setState({ period: e.target.value })}
                      required
                    />
                  </FormField>
                  <FormField label="Description">
                    <Textarea
                      rows={6}
                      value={this.state.description}
                      onChange={(e) => this.setState({ description: e.target.value })}
                    />
                  </FormField>
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>Entreprise</Card.Title>
                </Card.Header>
                <div className="space-y-4">
                  <FormField label="Nom de l'entreprise" required>
                    <Input
                      value={this.state.company}
                      onChange={(e) => this.setState({ company: e.target.value })}
                      required
                    />
                  </FormField>
                  <FormField label="Adresse">
                    <Input
                      value={this.state.companyAddress}
                      onChange={(e) => this.setState({ companyAddress: e.target.value })}
                    />
                  </FormField>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Téléphone">
                      <Input
                        value={this.state.companyPhone}
                        onChange={(e) => this.setState({ companyPhone: e.target.value })}
                      />
                    </FormField>
                    <FormField label="Email">
                      <Input
                        type="email"
                        value={this.state.companyEmail}
                        onChange={(e) => this.setState({ companyEmail: e.target.value })}
                      />
                    </FormField>
                  </div>
                  <FormField label="Site web">
                    <Input
                      type="url"
                      value={this.state.companyWebsite}
                      onChange={(e) => this.setState({ companyWebsite: e.target.value })}
                    />
                  </FormField>
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>Stack technique</Card.Title>
                  <Card.Description>Sépare avec Entrée ou virgule.</Card.Description>
                </Card.Header>
                <div className="space-y-4">
                  <FormField label="Technologies">
                    <TagInput
                      value={this.state.technologies}
                      onChange={(technologies) => this.setState({ technologies })}
                      placeholder="HTML, CSS, TypeScript…"
                    />
                  </FormField>
                  <FormField label="Frameworks">
                    <TagInput
                      value={this.state.frameworks}
                      onChange={(frameworks) => this.setState({ frameworks })}
                      placeholder="React, Next.js, Express…"
                    />
                  </FormField>
                  <FormField label="Bases de données">
                    <TagInput
                      value={this.state.databases}
                      onChange={(databases) => this.setState({ databases })}
                      placeholder="PostgreSQL, SQLite…"
                    />
                  </FormField>
                  <FormField label="Projets">
                    <TagInput
                      value={this.state.projects}
                      onChange={(projects) => this.setState({ projects })}
                      placeholder="Nom de projet…"
                    />
                  </FormField>
                </div>
              </Card>
            </div>

            {/* Sidebar : options */}
            <div className="space-y-5">
              <Card>
                <Card.Header>
                  <Card.Title>Options</Card.Title>
                </Card.Header>
                <div className="space-y-4">
                  <FormField label="Image (URL)" hint={<ImageIcon className="w-3.5 h-3.5" />}>
                    <Input
                      type="url"
                      placeholder="https://…"
                      value={this.state.imageUrl}
                      onChange={this.handleImageUrlChange}
                    />
                    {this.state.imageUrl && (
                      <div className="mt-2">
                        {this.state.imageError ? (
                          <div className="flex items-start gap-2 p-3 rounded-md bg-danger-bg text-danger text-xs">
                            <AlertCircle className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                            Impossible de charger l'image
                          </div>
                        ) : this.state.imagePreview ? (
                          <img
                            src={this.state.imagePreview}
                            alt="Aperçu"
                            className="w-full max-h-40 object-cover rounded-md border border-border"
                            onError={() => this.setState({ imageError: true, imagePreview: null })}
                          />
                        ) : null}
                      </div>
                    )}
                  </FormField>
                  <FormField label="Ordre d'affichage" helper="0 = premier.">
                    <NumberInput
                      value={Number(this.state.order) || 0}
                      onChange={(value) => this.setState({ order: value })}
                      min={0}
                    />
                  </FormField>
                </div>
              </Card>

              <Card variant="filled">
                <div className="space-y-2">
                  <Button type="submit" fullWidth isLoading={saving}>
                    {isEdit ? "Enregistrer les modifications" : "Créer le stage"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    fullWidth
                    onClick={() => this.props.history.push("/dashboard/stages")}
                  >
                    Annuler
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </form>
      </AdminPageShell>
    );
  }
}

export default withRouter(StageForm);
